import { ERC20__factory, getConnection, getProvider } from '@optimex-xyz/trade-wallet-sdk'
import { PublicKey } from '@solana/web3.js'
import { useQuery } from '@tanstack/react-query'

import { TANSTACK_QUERY_KEY } from '@/constants/tanstack-query'
import { useNativeToken } from '@/context'
import { useWallet } from '@/hooks/use-wallet'
import { IToken } from '@/types'
import { getBitcoinBalance, getEVMBalance, isBtcChain, isEvmChain, isNativeToken, isSolanaChain } from '@/utils'

import { ethers } from 'ethers'

interface UseTokenBalanceProps {
  token: IToken | null | undefined
  walletAddress?: string
}

export const fetchTokenBalance = async ({
  token,
  walletAddress,
  isConnected,
}: {
  token: IToken | null | undefined
  walletAddress: string
  isConnected: boolean
}) => {
  if (!token || !walletAddress || !isConnected) {
    return null
  }

  if (isNativeToken(token)) {
    let balanceResult: string | number | bigint = '0'
    if (isEvmChain(token.networkId)) {
      balanceResult = await getEVMBalance(token.networkId, walletAddress)
    }
    if (isBtcChain(token.networkId)) {
      balanceResult = await getBitcoinBalance(token.networkId, walletAddress)
    }
    if (isSolanaChain(token.networkId)) {
      const connection = getConnection(token.networkId)
      balanceResult = await connection.getBalance(new PublicKey(walletAddress))
    }
    return ethers.formatUnits(balanceResult?.toString() || '0', token.tokenDecimals || 0)
  }

  if (isSolanaChain(token.networkId)) {
    const connection = getConnection(token.networkId)
    const tokenPublicKey = new PublicKey(token.tokenAddress)
    const walletPublicKey = new PublicKey(walletAddress)
    const tokenAccounts = await connection.getParsedTokenAccountsByOwner(walletPublicKey, {
      mint: tokenPublicKey,
    })
    if (!tokenAccounts.value.length) {
      return '0'
    }
    const balance = tokenAccounts.value[0].account.data.parsed.info.tokenAmount.amount
    return ethers.formatUnits(BigInt(balance), token.tokenDecimals || 0).toString()
  }

  if (isEvmChain(token.networkId)) {
    const provider = getProvider(token.networkId)
    const contract = ERC20__factory.connect(token.tokenAddress, provider)
    const balanceAddress = await contract.balanceOf(walletAddress)
    return ethers.formatUnits(balanceAddress, token.tokenDecimals || 0)
  }

  return null
}

export const useNativeBalance = (networkId: string) => {
  const native = useNativeToken(networkId)
  const { data: balance } = useTokenBalance({ token: native })
  return { balance, native }
}

export const useTokenBalance = ({ token, walletAddress: _walletAddress }: UseTokenBalanceProps) => {
  const { wallets } = useWallet()
  const networkId = token?.networkId ?? ''

  const isConnected = !!_walletAddress || wallets[networkId]?.isConnected
  const walletAddress = _walletAddress || wallets?.[networkId]?.address

  return useQuery({
    queryKey: [TANSTACK_QUERY_KEY.WALLET.GET_TOKEN_BALANCE, networkId, token?.tokenAddress, walletAddress, isConnected],
    queryFn: () => fetchTokenBalance({ token, walletAddress, isConnected }),
    enabled: Boolean(token?.tokenAddress && walletAddress),
    staleTime: 30_000,
    gcTime: 30_000,
    refetchInterval: 5000,
    retry: 2,
  })
}
